import { useCallback, useRef, useState } from 'react';
import { WatchlistService } from '../services/watchlistService';
import { feedTitleKey } from '@aluvo/constants/src/homeFeed';

/**
 * Watchlist state for home feed titles (docs/home-feed.md → Add to watchlist).
 *
 * The server marks each title `saved`; this layers the viewer's actions on top:
 *   - a save shows as saved at once and is sent through the existing
 *     POST /watchlist (one request per title at a time);
 *   - 409 (already saved, e.g. from another tab) counts as saved;
 *   - any other failure rolls the title back and records an error for it, so
 *     the card can say so and offer to try again.
 */
export function useFeedWatchlist({ onAdded } = {}) {
    const [state, setState] = useState({}); // key → 'saving' | 'saved' | 'error'
    const inFlight = useRef(new Set());

    const statusOf = useCallback((title) => {
        const local = state[feedTitleKey(title)];
        if (local === 'saving' || local === 'saved') return 'saved';
        if (title?.saved) return 'saved';
        return local === 'error' ? 'error' : 'idle';
    }, [state]);

    const save = useCallback(async (title, analytics = {}) => {
        const key = feedTitleKey(title);
        if (title?.saved || inFlight.current.has(key) || state[key] === 'saved') return;
        inFlight.current.add(key);
        setState((prev) => ({ ...prev, [key]: 'saving' }));
        try {
            await WatchlistService.addToWatchlist(title.tmdbId, title.kind);
            setState((prev) => ({ ...prev, [key]: 'saved' }));
            onAdded?.(title, analytics);
        } catch (err) {
            const alreadySaved = err?.statusCode === 409;
            setState((prev) => ({ ...prev, [key]: alreadySaved ? 'saved' : 'error' }));
        } finally {
            inFlight.current.delete(key);
        }
    }, [state, onAdded]);

    return { statusOf, save };
}
